import React, { createContext, useCallback, useContext, useState } from 'react';
import { Toast } from '@koridor/design-system';

type ToastTone = 'neutral' | 'success' | 'warning' | 'danger' | 'accent';

interface ToastItem {
  id: number;
  tone: ToastTone;
  title: string;
  message?: string;
}

interface ToastContextValue {
  showToast: (opts: { tone?: ToastTone; title: string; message?: string }) => void;
  showError: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const showToast = useCallback((opts: { tone?: ToastTone; title: string; message?: string }) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t.slice(-2), { id, tone: opts.tone ?? 'neutral', title: opts.title, message: opts.message }]);
    setTimeout(() => dismiss(id), 4000);
  }, [dismiss]);

  const showError = useCallback((message: string) => {
    showToast({ tone: 'danger', title: message });
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, showError }}>
      {children}
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
        padding: '0 var(--gutter)', zIndex: 2000, pointerEvents: 'none',
      }}>
        {toasts.map((t) => (
          <div key={t.id} style={{ pointerEvents: 'auto', width: '100%', maxWidth: 'var(--screen-max)' }}>
            <Toast tone={t.tone} title={t.title} onClose={() => dismiss(t.id)}>
              {t.message}
            </Toast>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast outside ToastProvider');
  return ctx;
}
